/**
 *
 *  Referral Modal
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 01/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import { ThemeContext } from "../ThemeProvider/ThemeProvider";

// Components
// import Button from "../Button/Button";
import TBKTooltip from "../TruBeTooltip/TruBeTooltip";

// Icons
import CloseIcon from "../../icons/CloseIcon";
// import CopyIcon from "../../icons/CopyIcon";
import FacebookIcon from "../../icons/FacebookIcon";
import TwitterIcon from "../../icons/TwitterIcon";
import WhatsAppIcon from "../../icons/WhatsAppIcon";
import ChainIcon from "../../icons/ChainIcon";
import CheckNiceIcon from "../../icons/CheckNiceIcon";
import GiftIcon from "../../icons/GiftIcon";
// import EmailIcon from "./EmailIcon";

// Utils
import { makeCancelable, clientWidth } from "../../utils";
import copy from "../../utils/copy";
import share from "../../utils/share";
import { modalStyles } from "../../constants";

// import {
//   Analytics,
//   SHARE_CATEGORY,
//   EVENT_SHARE_FACEBOOK,
//   EVENT_SHARE_TWITTER,
//   // EVENT_SHARE_INSTAGRAM,
//   EVENT_SHARE_WHATSAPP,
//   EVENT_SHARE_EMAIL,
//   EVENT_SHARE_LINK,
//   EVENT_SHARE_CLIPBOARD,
// }from 'mainApp/Analytics';

const AUTOHIDE_TIME = 3000;

export class ReferralModal extends React.Component {
  static propTypes = {
    modalTitle: PropTypes.string.isRequired,
    modalText: PropTypes.string.isRequired,
    shareTitle: PropTypes.string,
    shareText: PropTypes.string,
    shareLabel: PropTypes.string.isRequired,
    shareInput: PropTypes.string.isRequired, // textToCopy
    shareUrl: PropTypes.string,
    copyFeedbackText: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
  };

  static displayName = "ReferralModal";
  static contextType = ThemeContext;

  static defaultProps = {
    modalTitle: "Share TruBe with a Friend",
    modalText:
      "Share the link to refer a friend and receive $10 Referral Credit when they join TruBe and book their first session. Your friend will also get a $10 Referral Credit for joining. Sharing is Caring!",
    shareLabel: "Your Referral Link:",
  };

  constructor(props) {
    super(props);

    this.inputRef = React.createRef(null);

    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.handleShareFacebookClick = this.handleShareFacebookClick.bind(this);
    this.handleShareTwitterClick = this.handleShareTwitterClick.bind(this);
    this.handleShareWhatsAppClick = this.handleShareWhatsAppClick.bind(this);
    this.handleShareEmailClick = this.handleShareEmailClick.bind(this);
    this.handleCopyToClipboard = this.handleCopyToClipboard.bind(this);
    this.showCopyTooltip = this.showCopyTooltip.bind(this);
    this.hideCopyTooltip = this.hideCopyTooltip.bind(this);

    this.state = {
      isModalOpen: false,
      isCopyTooltipShow: false,
    };
  }

  openModal() {
    return new Promise((resolve) => {
      this.modalResolve = resolve;

      this.setState({
        isModalOpen: true,
      });
    });
  }

  closeModal() {
    if (this.modalResolve) {
      this.modalResolve();
      this.modalResolve = null;

      this.props.onClose();
    }

    this.setState({
      isModalOpen: false,
    });
  }

  handleShareFacebookClick(e) {
    share.openFacebookShareWindow();

    // Analytics.trackEvent(SHARE_CATEGORY, '', EVENT_SHARE_FACEBOOK);
  }

  handleShareTwitterClick(e) {
    share.openTwitterShareWindow();

    // Analytics.trackEvent(SHARE_CATEGORY, '', EVENT_SHARE_TWITTER);
  }

  handleShareWhatsAppClick(e) {
    share.openWhatsAppShareWindow();

    // Analytics.trackEvent(SHARE_CATEGORY, '', EVENT_SHARE_WHATSAPP);
  }

  handleShareEmailClick(e) {
    var shareText = this.props.shareText; // `Book a session with ${trainer.firstName} ${trainer.familyName} in London: ${trainer.partnerProducts && trainer.partnerProducts.filter(p => p.name).map(p => p.name).join(', ')}`;

    share.openEmailShareWindow(shareText);

    // Analytics.trackEvent(SHARE_CATEGORY, '', EVENT_SHARE_EMAIL);
  }

  handleCopyToClipboard(e) {
    const { shareUrl } = this.props;

    if (copy(shareUrl)) {
      this.showCopyTooltip();

      // Analytics.trackEvent(SHARE_CATEGORY, '', target.tagName.toLocaleLowerCase() === 'button' ? EVENT_SHARE_LINK : EVENT_SHARE_CLIPBOARD);
    }
  }

  showCopyTooltip() {
    if (this.hideCancelablePromise) {
      return;
    }

    // Auto hide after 4 sec
    var hidePromise = new Promise((resolve, reject) => {
      this.setState({ isCopyTooltipShow: true });

      setTimeout(() => {
        resolve();
      }, AUTOHIDE_TIME);
    });

    this.hideCancelablePromise = makeCancelable(hidePromise);

    return this.hideCancelablePromise.promise.then(() => {
      this.hideCancelablePromise = null;
      this.hideCopyTooltip();
    });
  }

  hideCopyTooltip() {
    if (this.hideCancelablePromise) {
      this.hideCancelablePromise.cancel();
      this.hideCancelablePromise = null;
    }

    this.setState({ isCopyTooltipShow: false });
  }

  componentDidMount() {
    share.init(
      this.props.shareTitle,
      this.props.shareText,
      this.props.shareUrl
    );
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.openModal();
    } else if (!this.props.isOpen && prevProps.isOpen) {
      this.closeModal();
    }

    if (
      prevProps.shareTitle !== this.props.shareTitle ||
      prevProps.shareUrl !== this.props.shareUrl
    ) {
      share.init(
        this.props.shareTitle,
        this.props.shareText,
        this.props.shareUrl
      );
    }
  }

  render() {
    const defaultCustomStyle =
      clientWidth() < this.context.theme.responsive.breakpoints.sm
        ? Object.assign(
            {},
            { ...modalStyles.mobileDefault },
            {
              overlay: {
                ...modalStyles.mobileDefault.overlay,
                zIndex: 1060,
              },
            }
          )
        : Object.assign(
            {},
            { ...modalStyles.default },
            {
              overlay: { ...modalStyles.default.overlay, zIndex: 1060 },
              content: {
                ...modalStyles.default.content,
                width: "428px",
                zIndex: 1060,
              },
            }
          );

    return (
      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={this.state.isModalOpen}
        onAfterOpen={this.afterOpenModal}
        onRequestClose={this.closeModal}
        style={defaultCustomStyle}
        contentLabel="Share Referral Modal"
      >
        <button
          onClick={this.closeModal}
          className="tbk-absolute tbk-top-2 tbk-right-2 tbk-z-10 tbk-text-coral"
        >
          <CloseIcon />
        </button>
        <div className="share-referral-modal tbk-relative tbk-rounded-lg tbk-bg-basic-white tbk-p-3 md:tbk-p-0">
          <div className="tbk-text-title-bold tbk-t-0 tbk-mb-2 tbk-flex tbk-items-center tbk-text-primary">
            <GiftIcon className="tbk-mr-1 tbk-shrink-0 tbk-drop-shadow-trube" />
            {this.props.modalTitle}
          </div>
          <p className="tbk-text-main tbk-mb-2 tbk-text-grey">
            {this.props.modalText}
          </p>
          <div className="tbk-mb-2">
            <label
              htmlFor="inputtext"
              className="tbk-text-small tbk-mb-0.5 tbk-text-primary"
            >
              {this.props.shareLabel}
            </label>
            <div className="with-tooltip tbk-relative">
              <input
                ref={this.inputRef}
                className={`tbk-input-field tbk-text-main tbk-mb-0 tbk-grow tbk-text-ellipsis tbk-border-blue-grey-light tbk-bg-basic-white tbk-pt-[11px] tbk-pb-[11px] tbk-text-primary`}
                name="inputtext"
                defaultValue={this.props.shareInput}
                readOnly
              />
              {/*<button*/}
              {/*  className="tbk-absolute tbk-right-2 tbk-bottom-1.5 tbk-border-none tbk-p-0 tbk-leading-none tbk-text-coral"*/}
              {/*  id="copy-referral-inline-button"*/}
              {/*  onClick={this.handleCopyToClipboard}*/}
              {/*>*/}
              {/*  <CopyIcon />*/}
              {/*</button>*/}
              <TBKTooltip
                className={"please don't look here ;)"}
                element={this.inputRef.current}
                adjust
                inside
                placement="top"
                isShow={this.state.isCopyTooltipShow}
                onClick={this.hideCopyTooltip}
              >
                <div className="tbk-flex tbk-items-center">
                  <CheckNiceIcon className="tbk-mr-0.5 tbk-text-green" />{" "}
                  <div className="tbk-mr-0.5">
                    {this.props.copyFeedbackText}
                  </div>
                </div>
              </TBKTooltip>
            </div>
          </div>
          <div className="share-modal-social-buttons-wrapper tbk-grid tbk-grid-cols-4 tbk-justify-between tbk-gap-0.5 md:tbk-gap-2">
            <button
              className="tbk-rounded-2xl tbk-border tbk-border-transparent tbk-bg-blue-grey-light tbk-px-0.5 tbk-py-2 tbk-text-center hover:tbk-border-solid hover:tbk-border-blue-grey hover:tbk-bg-basic-white hover:tbk-shadow-trube active:tbk-border-blue-grey-light active:tbk-bg-grey-light lg:tbk-px-1.5"
              onClick={this.handleCopyToClipboard}
            >
              <ChainIcon className="tbk-inline-block tbk-text-primary" />
              <div className="tbk-text-small tbk-whitespace-nowrap tbk-text-blue-grey">
                Copy Link
              </div>
            </button>
            <button
              className="tbk-rounded-2xl tbk-border tbk-border-transparent tbk-bg-blue-grey-light tbk-px-0.5 tbk-py-2 tbk-text-center hover:tbk-border-solid hover:tbk-border-blue-grey hover:tbk-bg-basic-white hover:tbk-shadow-trube active:tbk-border-blue-grey-light active:tbk-bg-grey-light lg:tbk-px-1.5"
              onClick={this.handleShareWhatsAppClick}
            >
              <WhatsAppIcon className="tbk-inline-block tbk-text-primary" />
              <div className="tbk-text-small tbk-text-blue-grey">WhatsApp</div>
            </button>
            <button
              className="tbk-rounded-2xl tbk-border tbk-border-transparent tbk-bg-blue-grey-light tbk-px-0.5 tbk-py-2 tbk-text-center hover:tbk-border-solid hover:tbk-border-blue-grey hover:tbk-bg-basic-white hover:tbk-shadow-trube active:tbk-border-blue-grey-light active:tbk-bg-grey-light lg:tbk-px-1.5"
              onClick={this.handleShareFacebookClick}
            >
              <FacebookIcon className="tbk-inline-block tbk-text-primary" />
              <div className="tbk-text-small tbk-text-blue-grey">Facebook</div>
            </button>
            <button
              className="tbk-rounded-2xl tbk-border tbk-border-transparent tbk-bg-blue-grey-light tbk-px-0.5 tbk-py-2 tbk-text-center hover:tbk-border-solid hover:tbk-border-blue-grey hover:tbk-bg-basic-white hover:tbk-shadow-trube active:tbk-border-blue-grey-light active:tbk-bg-grey-light lg:tbk-px-1.5"
              onClick={this.handleShareTwitterClick}
            >
              <TwitterIcon className="tbk-inline-block tbk-text-primary" />
              <div className="tbk-text-small tbk-text-blue-grey">Twitter</div>
            </button>

            {/*<button*/}
            {/*  className="tbk-rounded-lg tbk-bg-basic-white tbk-p-2 tbk-shadow-trube lg:tbk-p-4"*/}
            {/*  onClick={this.handleShareEmailClick}*/}
            {/*>*/}
            {/*  <EmailIcon />*/}
            {/*</button>*/}
          </div>

          {/*<Button*/}
          {/*  className="tb-button tbk-w-full"*/}
          {/*  onClick={this.handleCopyToClipboard}*/}
          {/*>*/}
          {/*  Share link*/}
          {/*</Button>*/}
        </div>
      </Modal>
    );
  }
}

export default ReferralModal;
